<template>
    <div ref="rootEl" class="relative">
        <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-50"
            aria-label="Row actions"
            @click.stop="toggle"
        >
            <EllipsisVerticalIcon class="h-5 w-5" />
        </button>

        <div
            v-if="open"
            class="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            @click.stop
        >
            <div class="py-1">
                <template v-if="items?.length">
                    <button
                        v-for="(it, idx) in items"
                        :key="idx"
                        type="button"
                        :disabled="!!it.disabled"
                        class="flex w-full items-center justify-between px-3 py-2 text-left text-sm"
                        :class="itemClass(it)"
                        @click.stop="onItemClick(it)"
                    >
                        <span class="truncate">{{ it.label }}</span>
                    </button>
                </template>

                <div v-else class="px-3 py-2 text-sm text-gray-400">
                    No actions
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { router } from "@inertiajs/vue3";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/outline";

export type KebabItem<Row = any> = {
    label: string;
    href?: string | ((row: Row) => string);
    action?: (row: Row) => void | Promise<void>;
    disabled?: boolean;
    danger?: boolean;
};

const props = defineProps<{
    items: KebabItem[];
    row: any;
}>();

const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);

const toggle = () => {
    open.value = !open.value;
};

const close = () => {
    open.value = false;
};

const onDocClick = (e: MouseEvent) => {
    if (!open.value) return;
    const target = e.target as Node | null;
    if (!target) return;
    if (rootEl.value && !rootEl.value.contains(target)) close();
};

onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));

const itemClass = (it: KebabItem) => {
    if (it.disabled) return "cursor-not-allowed text-gray-300";
    if (it.danger) return "text-red-600 hover:bg-red-50";
    return "text-gray-700 hover:bg-gray-50";
};

const onItemClick = async (it: KebabItem) => {
    if (it.disabled) return;

    if (it.href) {
        const href =
            typeof it.href === "function" ? it.href(props.row) : it.href;
        if (href) router.visit(href);
        close();
        return;
    }

    if (it.action) {
        await it.action(props.row);
        close();
    }
};
</script>
